import type { ListBuilderConfiguration } from '../types';

/**
 * Purchase Order Receipt list configuration
 * Shows purchase order receipts with date columns and edit/delete actions
 * Converted from JSON to TypeScript for type safety
 */
export const PURCHASE_ORDER_RECEIPT_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-purchase-order-receipts-all',
			cacheTTL: 600000,
			name: 'All Purchase Order Receipts',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'receipt-number',
					fields: [
						{
							id: 'receipt-number-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'displayId',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'statusInfo',
								},
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Receipt #' }],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'displayId',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'receipt-date',
					fields: [
						{
							id: 'receipt-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'receiptDate',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h2', text: 'Receipt Date' }],
					minWidth: 140,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'receiptDate',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'last-import',
					fields: [
						{
							id: 'last-import-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'lastImportDate',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h3', text: 'Last Import' }],
					minWidth: 140,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'lastImportDate',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'created-date',
					fields: [
						{
							id: 'created-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: '_created_date',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h4', text: 'Created' }],
					minWidth: 140,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: '_created_date',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'middle',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'edit',
									callbackName: 'edit',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [{ id: 'actions-header', text: '' }],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			description: 'List of purchase order receipts',
			defaultSortId: 'column-receipt-date-desc',
			requiredFields: [
				'purchaseOrderReceipt._id',
				'purchaseOrderReceipt.externalId',
				'purchaseOrderReceipt.receiptDate',
				'purchaseOrderReceipt.lastImportDate',
				'purchaseOrderReceipt._created_date',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/purchase-orders/:purchaseOrderId/receipts/:id',
			objectLabel: 'Purchase Order Receipt',
			objectTable: 'purchase_order_receipt',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'receiptDate', fieldLabel: 'Receipt Date' },
				{ fieldName: 'lastImportDate', fieldLabel: 'Last Import Date' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
			],
		},
	],
	tableSorts: [
		{
			id: 'receipt-number',
			name: 'Receipt Number',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'externalId',
				},
			],
			description: 'Sort by receipt number',
		},
		{
			id: 'receipt-date',
			name: 'Receipt Date',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'receiptDate',
				},
			],
			description: 'Sort by receipt date (newest first)',
		},
		{
			id: 'created-date',
			name: 'Created Date',
			fields: [
				{
					direction: 'DESC',
					fieldName: '_created_date',
				},
			],
			description: 'Sort by created date (newest first)',
		},
	],
};
